import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from 'recharts';
import { API_URL } from '../config';
import { authenticatedFetch } from '../utils/api';

const seasonColorMap = {
  green: '#00ED64',
  yellow: '#F0AD4E',
  red: '#DB3030',
};

const formatCurrencyCompact = (num) => {
  if (!Number.isFinite(num)) return '$0K';
  if (num >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
  if (num >= 1e3) return `$${(num / 1e3).toFixed(0)}K`;
  return `$${num.toFixed(0)}`;
};

/** Whole PT values only (tiles, PT chart tooltips, table PT columns). */
const formatPtsInteger = (n) => {
  if (!Number.isFinite(n)) return '—';
  return Math.round(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
};

const formatDeltaPts = (d) => {
  const v = Math.round(Number(d));
  if (!Number.isFinite(v)) return '—';
  return `${v > 0 ? '+' : ''}${v.toLocaleString('en-US')}`;
};

const displayChannelName = (name) => (name || '').replace(/_/g, ' ');

/** Sentinel for chart dropdown: aggregate across all channels (matches `monthly` from API). */
const ALL_CHANNELS = '__all__';

const RetrospectiveOptimalPacing = () => {
  const [chartMode, setChartMode] = useState('spend'); // 'spend' | 'pts'
  const [selectedRegion, setSelectedRegion] = useState('GLOBAL');
  const [selectedChannel, setSelectedChannel] = useState(ALL_CHANNELS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [monthlyByChannel, setMonthlyByChannel] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const [channels, setChannels] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const regionParam = encodeURIComponent(selectedRegion);
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const url = `${API_URL}/api/retro-pacing?region=${regionParam}`;
        const data = await authenticatedFetch(url);
        if (cancelled) return;
        if (data.debug) {
          console.debug('[Retrospective Optimal Pacing] API file debug:', data.debug);
        }
        if (data.error) {
          setError(data.error);
          setSummary(null);
          setMonthly([]);
          setMonthlyByChannel([]);
          setChannelOptions([]);
          setChannels([]);
        } else {
          setSummary(data.summary || null);
          setMonthly(Array.isArray(data.monthly) ? data.monthly : []);
          setMonthlyByChannel(Array.isArray(data.monthly_by_channel) ? data.monthly_by_channel : []);
          setChannelOptions(Array.isArray(data.channel_options) ? data.channel_options : []);
          setChannels(Array.isArray(data.channels) ? data.channels : []);
          setSelectedChannel(ALL_CHANNELS);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load retrospective pacing data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedRegion]);

  const chartData = useMemo(() => {
    let rows = monthly;
    if (selectedChannel !== ALL_CHANNELS) {
      rows = monthlyByChannel
        .filter((r) => r.channel === selectedChannel)
        .slice()
        .sort((a, b) => String(a.month_key).localeCompare(String(b.month_key)));
    }
    return rows.map((row) => {
      const base = {
        month: row.month_label || row.month_key,
        seasonality_pct: row.seasonality_pct,
        seasonal_color: row.seasonal_color,
      };
      if (chartMode === 'spend') {
        return { ...base, actual: row.actual_spend, optimal: row.optimal_spend };
      }
      return { ...base, actual: row.actual_pts, optimal: row.optimal_pts };
    });
  }, [monthly, monthlyByChannel, selectedChannel, chartMode]);

  const chartTitle =
    chartMode === 'spend' ? 'Monthly Spend: Actual vs Optimal' : 'Monthly PTs: Actual vs Optimal';
  const tooltipFormatter = (value) =>
    chartMode === 'spend'
      ? formatCurrencyCompact(value)
      : formatPtsInteger(Number(value));

  const actualFill = '#3D4F58';
  const optimalFill = '#00ED64';
  const getSeasonBg = (color) => seasonColorMap[String(color).toLowerCase()] || '#5C6C75';

  const missedMain =
    summary && Number.isFinite(summary.missed_opportunity)
      ? `${summary.missed_opportunity >= 0 ? '+' : ''}${formatPtsInteger(summary.missed_opportunity)} Pts`
      : '—';

  const missedSub =
    summary &&
    summary.missed_opportunity_pct != null &&
    Number.isFinite(summary.missed_opportunity_pct)
      ? `${summary.missed_opportunity_pct >= 0 ? '+' : ''}${summary.missed_opportunity_pct.toFixed(1)}% lift, same budget`
      : null;

  return (
    <div className="min-h-full bg-mongodb-gray-light2 px-4 py-6">
      {/* Header — same structure as Quarterly Budget Planner */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-mongodb-gray-dark3 mb-1">Retrospective Optimal Pacing</h1>
          <p className="text-mongodb-gray-dark1 text-sm">
             Same total budget, optimally redistributed across channels
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
            className="px-4 py-2 border border-mongodb-green-dark2 rounded-lg bg-white text-mongodb-gray-dark3 focus:outline-none focus:ring-2 focus:ring-mongodb-green-dark2 shadow-sm font-medium"
          >
            <option value="GLOBAL">Global (All Regions)</option>
            <option value="APAC">APAC</option>
            <option value="EMEA">EMEA</option>
            <option value="INDIA">India</option>
            <option value="LATAM">LATAM</option>
            <option value="North America">North America</option>
          </select>
          {selectedRegion !== 'GLOBAL' && (
            <button
              type="button"
              onClick={() => setSelectedRegion('GLOBAL')}
              className="px-4 py-2 text-sm text-mongodb-green-dark2 hover:text-mongodb-green-dark1 font-medium"
            >
              Reset to Global View
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="mb-5">
        <div className="flex flex-wrap gap-4 justify-start max-w-3xl">
          <div className="w-[200px] shrink-0 h-[92px] bg-white rounded-2xl p-5 shadow-md border border-mongodb-gray-light2 flex flex-col justify-center">
            <div className="text-xs font-semibold text-mongodb-gray-dark1 uppercase tracking-wider mb-2">ACTUAL PTS</div>
            <div className="text-3xl font-bold text-mongodb-gray-dark3">
              {loading ? '…' : summary ? formatPtsInteger(summary.actual_pts_total) : '—'}
            </div>
          </div>

          <div className="w-[200px] shrink-0 h-[92px] bg-white rounded-2xl p-5 shadow-md border border-mongodb-gray-light2 flex flex-col justify-center">
            <div className="text-xs font-semibold text-mongodb-gray-dark1 uppercase tracking-wider mb-2">OPTIMAL PTS</div>
            <div className="text-3xl font-bold text-mongodb-green-dark2">
              {loading ? '…' : summary ? formatPtsInteger(summary.optimal_pts_total) : '—'}
            </div>
          </div>

          <div className="w-[240px] shrink-0 h-[92px] bg-white rounded-2xl p-5 shadow-md border border-mongodb-gray-light2 flex flex-col justify-center">
            <div className="text-xs font-semibold text-mongodb-gray-dark1 uppercase tracking-wider mb-2">MISSED OPPORTUNITY</div>
            <div className="text-3xl font-bold text-mongodb-green-dark2">{loading ? '…' : missedMain}</div>
            {missedSub && <div className="mt-1 text-sm text-mongodb-gray-dark1">{missedSub}</div>}
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <label htmlFor="retro-channel-select" className="sr-only">
            Channel
          </label>
          <select
            id="retro-channel-select"
            value={selectedChannel}
            onChange={(e) => setSelectedChannel(e.target.value)}
            className="min-w-[200px] max-w-[min(100vw-2rem,320px)] px-4 py-2 border border-mongodb-gray-light2 rounded-lg bg-white text-mongodb-gray-dark3 shadow-sm font-medium cursor-pointer"
            disabled={loading || !!error}
          >
            <option value={ALL_CHANNELS}>All channels</option>
            {channelOptions.map((ch) => (
              <option key={ch} value={ch}>
                {displayChannelName(ch)}
              </option>
            ))}
          </select>
        </div>

        <div className="inline-flex rounded-lg border border-mongodb-gray-light2 bg-white p-1">
          <button
            type="button"
            onClick={() => setChartMode('spend')}
            className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors cursor-pointer ${
              chartMode === 'spend'
                ? 'bg-mongodb-green-dark2 text-white'
                : 'text-mongodb-gray-dark3 hover:bg-mongodb-gray-light2'
            }`}
          >
            $ Spend
          </button>
          <button
            type="button"
            onClick={() => setChartMode('pts')}
            className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors cursor-pointer ${
              chartMode === 'pts'
                ? 'bg-mongodb-green-dark2 text-white'
                : 'text-mongodb-gray-dark3 hover:bg-mongodb-gray-light2'
            }`}
          >
            PTs
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-5 border border-mongodb-gray-light2 shadow-md mb-4">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <div className="text-mongodb-gray-dark3 text-lg font-semibold">{chartTitle}</div>
          </div>

          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: actualFill }} />
              <span className="text-mongodb-gray-dark3">Actual</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: optimalFill }} />
              <span className="text-mongodb-gray-dark3">Optimal</span>
            </div>
          </div>
        </div>

        <div className="h-[22rem] relative">
          <div className="absolute top-2 left-0 right-0 flex items-center justify-center gap-5 px-6 pointer-events-none">
            <span className="flex items-center gap-1 text-xs font-semibold text-mongodb-gray-dark1">
              <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#00ED64' }} /> High
            </span>
            <span className="flex items-center gap-1 text-xs font-semibold text-mongodb-gray-dark1">
              <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#F0AD4E' }} /> Medium
            </span>
            <span className="flex items-center gap-1 text-xs font-semibold text-mongodb-gray-dark1">
              <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#DB3030' }} /> Low
            </span>
          </div>

          <div className="absolute top-8 left-0 right-0 px-6" style={{ zIndex: 10 }}>
            <div className="flex h-[6px] gap-px">
              {chartData.map((d, idx) => (
                <div
                  key={`season-${idx}`}
                  className="flex-1 relative group cursor-pointer rounded-sm"
                  style={{ background: getSeasonBg(d.seasonal_color) }}
                >
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-[#1C2D38] text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                    {d.month}: {d.seasonality_pct != null ? d.seasonality_pct.toFixed(2) : '—'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {loading && (
            <div className="flex h-full items-center justify-center pt-10 text-mongodb-gray-dark1 text-sm">
              Loading chart…
            </div>
          )}

          {!loading && chartData.length === 0 && (
            <div className="flex h-full items-center justify-center pt-10 text-mongodb-gray-dark1 text-sm">
              No monthly data available.
            </div>
          )}

          {!loading && chartData.length > 0 && (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 40, right: 20, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#C1C7C6" />
                <XAxis
                  dataKey="month"
                  tick={{ fill: '#3D4F58', fontSize: 11, fontWeight: 700 }}
                  tickLine={false}
                  axisLine={{ stroke: '#C1C7C6' }}
                  interval={0}
                  angle={chartData.length > 10 ? -35 : 0}
                  textAnchor={chartData.length > 10 ? 'end' : 'middle'}
                  height={chartData.length > 10 ? 70 : 40}
                  dy={6}
                />
                <Tooltip
                  contentStyle={{
                    background: '#1C2D38',
                    border: '1px solid #3D4F58',
                    color: '#F9FBFA',
                  }}
                  formatter={(value) => tooltipFormatter(value)}
                />
                <Bar dataKey="actual" name="Actual" fill={actualFill} radius={[8, 8, 0, 0]} />
                <Bar dataKey="optimal" name="Optimal" fill={optimalFill} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 border border-mongodb-gray-light2 shadow-md">
        <div className="text-mongodb-gray-dark3 text-lg font-semibold mb-4">Channel-Level Opportunity (Pacing Only)</div>

        <div className="overflow-x-auto">
          <table className="min-w-[820px] w-full border-collapse text-sm">
            <thead>
              <tr className="bg-mongodb-gray-light3 border-b border-mongodb-gray-light2">
                <th className="text-left font-semibold py-3 pr-3 text-mongodb-gray-dark3">Channel</th>
                <th className="text-right font-semibold py-3 px-3 text-mongodb-gray-dark3">Actual Pts</th>
                <th className="text-right font-semibold py-3 px-3 text-mongodb-gray-dark3">Optimal Pts</th>
                <th className="text-right font-semibold py-3 px-3 text-mongodb-gray-dark3">Delta</th>
                <th className="text-right font-semibold py-3 px-3 text-mongodb-gray-dark3">% Lift</th>
                <th className="text-left font-semibold py-3 pl-2 pr-4 text-mongodb-gray-dark3">Opportunity</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-mongodb-gray-dark1">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading &&
                channels.map((row) => (
                  <tr key={row.channel} className="border-b border-mongodb-gray-light2 hover:bg-mongodb-gray-light3/50">
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ background: optimalFill }} />
                        <span className="text-mongodb-gray-dark3 font-semibold">{displayChannelName(row.channel)}</span>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-right text-mongodb-gray-dark3 font-medium">
                      {formatPtsInteger(Number(row.actual_pts))}
                    </td>
                    <td className="py-3 px-3 text-right text-mongodb-gray-dark3 font-medium">
                      {formatPtsInteger(Number(row.optimal_pts))}
                    </td>
                    <td className="py-3 px-3 text-right text-mongodb-green-dark2 font-semibold">
                      {formatDeltaPts(row.delta)}
                    </td>
                    <td className="py-3 px-3 text-right text-mongodb-gray-dark3 font-medium">
                      {row.pct_lift > 0 ? `+${Number(row.pct_lift).toFixed(2)}%` : `${Number(row.pct_lift).toFixed(2)}%`}
                    </td>
                    <td className="py-3 pl-2 pr-4">
                      <div className="h-2 bg-mongodb-gray-light2 rounded-full overflow-hidden" style={{ width: 260 }}>
                        <div
                          className="h-full"
                          style={{
                            width: `${Math.max(0, Math.min(100, row.bar_width_pct))}%`,
                            background: '#00ED64',
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              {!loading && channels.length === 0 && !error && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-mongodb-gray-dark1">
                    No channel data.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RetrospectiveOptimalPacing;
